"use client";

import React, { useRef, useState } from 'react';
import { Form, Button } from '../elements';
import { DynamicFormFields } from '../components/DynamicFormFields';
import { firePixelEvent, setPixelUserData } from '../../tracking/firePixelEvent';
import { captureEvent } from '../../tracking/captureEvent';
import type { FormDefinition } from '../../types/api/form';
import { useFormDefinitions } from '../../next/contexts/form-definitions';

interface ContactSectionFormProps {
    /** Form fields are rendered from this definition (required). */
    formDefinition: FormDefinition | null | undefined;
    submitButtonText?: string;
    successMessage?: string;
    thankYouMessage?: string;
    onSuccess?: () => void;
    /** Optional URLs for ToS/Privacy links in consent checkbox (passed to DynamicFormFields). */
    privacyPolicyUrl?: string;
    termsOfServiceUrl?: string;
}

export const ContactSectionForm = ({
    formDefinition,
    submitButtonText = "Send message",
    successMessage = "Thank you for contacting us! We'll get back to you soon.",
    thankYouMessage,
    onSuccess,
    privacyPolicyUrl,
    termsOfServiceUrl,
}: ContactSectionFormProps) => {
    const { leadFormDefinition } = useFormDefinitions();
    const resolvedFormDefinition = formDefinition ?? leadFormDefinition;
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
    const [statusMessage, setStatusMessage] = useState<string>('');
    const formRef = useRef<HTMLFormElement>(null);

    if (
        !resolvedFormDefinition ||
        !Array.isArray(resolvedFormDefinition.fields) ||
        resolvedFormDefinition.fields.length === 0
    ) {
        return null;
    }

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus('idle');
        setStatusMessage('');

        const formData = new FormData(e.currentTarget);
        const data: Record<string, string> = { formType: 'lead' };
        formData.forEach((value, key) => {
            if (key.endsWith('_prefix')) return;
            if (typeof value === 'string') data[key] = value;
        });

        try {
            const response = await fetch('/api/form/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data),
            });
            const result = await response.json();

            if (result.success) {
                setSubmitStatus('success');
                setStatusMessage(result.message || successMessage);
                formRef.current?.reset();
                onSuccess?.();
                await setPixelUserData({ email: data.email, phone: data.phone });
                firePixelEvent('Lead', undefined, result.eventId);
                captureEvent('form_submitted', { form_type: 'lead', ...(result.eventId && { event_id: result.eventId }) });
                setTimeout(() => setSubmitStatus('idle'), 5000);
            } else {
                const errorMsg = result.error || 'Something went wrong. Please try again.';
                setSubmitStatus('error');
                setStatusMessage(errorMsg);
                captureEvent('form_failed', { form_type: 'lead', error: errorMsg });
            }
        } catch {
            setSubmitStatus('error');
            setStatusMessage('Network error. Please try again later.');
            captureEvent('form_failed', { form_type: 'lead', error: 'network_error' });
        }
        setIsSubmitting(false);
    };

    return (
        <Form ref={formRef} onSubmit={handleSubmit} className="flex flex-col gap-8">
            <div className="flex flex-col gap-6">
                <DynamicFormFields
                    form={resolvedFormDefinition}
                    privacyPolicyUrl={privacyPolicyUrl}
                    termsOfServiceUrl={termsOfServiceUrl}
                />
            </div>
            {submitStatus === 'success' && (
                <div className="rounded-lg bg-success-50 p-4 text-success-700">
                    {thankYouMessage ?? statusMessage}
                </div>
            )}
            {submitStatus === 'error' && (
                <div className="rounded-lg bg-error-50 p-4 text-error-700">{statusMessage}</div>
            )}
            <Button
                type="submit"
                size="xl"
                isDisabled={isSubmitting}
                isLoading={isSubmitting}
            >
                {isSubmitting ? 'Sending...' : submitButtonText}
            </Button>
        </Form>
    );
};
